'use client'

import IslandTropical from '@/icons/font-awesome/sharp-light/island-tropical.svg'
import { useTranslations } from 'next-intl'
import { CheckoutResult } from '@/components/pages/checkout/CheckoutResult'
import React, { useContext, useState } from 'react'
import { BookingPageContext } from '@/components/pages/checkout/booking-page/BookingPageContext'
import { createPayment } from '@/lib/payment/actions'
import { ConfigContext } from '@/components/common/ConfigContext'
import { LOCALE_MAPPING, NON_MOLLIE_PAYMENT_METHOD } from '@/lib/payment/constants'
import { PayNowButton } from '@/components/pages/checkout/PayNowButton'
import { CheckoutErrorMessage } from '@/components/pages/checkout/CheckoutErrorMessage'
import { BookingFormData, BookingRequestSelection, SafeBookingResponse } from '@/types/Booking'
import { Locale } from '@/lib/sharedConstants'
import { PaymentMethod } from '@mollie/api-client'

function getParameters(
  formValues: BookingFormData,
  bookingSelection: BookingRequestSelection,
  saveBookingResponse: SafeBookingResponse | undefined,
  locale: Locale,
) {
  return {
    billingAddress: {
      givenName: formValues.Customer.FirstName,
      familyName: formValues.Customer.LastName,
      email: formValues.Customer.Email,
      streetAndNumber: formValues.Customer.Address,
      postalCode: formValues.Customer.PostalCode,
      city: formValues.Customer.City,
      country: formValues.Customer.CountryCode,
    },
    method: bookingSelection.PaymentMethodCode!,
    amount: {
      value: saveBookingResponse!.PaymentAmount.toString(),
      currency: saveBookingResponse!.PaymentCurrency,
    },
    metadata: {
      orderId: saveBookingResponse!.OrderId,
      afterBooking: true,
      ordertype: saveBookingResponse?.OrderType,
    },
    locale: LOCALE_MAPPING[locale as string] as string,
  }
}

export function BookingSuccess(props: {
  message: string | null | undefined
  request: boolean
  reservation: boolean
}) {
  const t = useTranslations('Booking.BookingSuccess')
  const [errorMessage, setErrorMessage] = useState('')
  const { formHandler, withinIframe, saveBookingResponse, bookingSelection } =
    useContext(BookingPageContext)
  const locale = useContext(ConfigContext)!.locale
  const formValues = formHandler.getValues()
  const paymentAmount = saveBookingResponse?.PaymentAmount || 0
  const showPayNowButton =
    !NON_MOLLIE_PAYMENT_METHOD.includes(bookingSelection.PaymentMethodCode! as PaymentMethod) &&
    paymentAmount > 0

  const companyName = useContext(ConfigContext)!.companyNameJsonLd
  const buttonBlockedRef = React.useRef(false)
  const [buttonBlocked, setButtonBlocked] = useState(false)

  function handlePaymentClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    e.preventDefault()

    setButtonBlocked(true)
    const params = getParameters(formValues, bookingSelection, saveBookingResponse, locale)

    createPayment(params, companyName, locale)
      .then((result) => {
        if (result.checkoutUrl) {
          if (withinIframe) {
            window.open(result.checkoutUrl, '_blank')
            window.close()
          } else {
            window.location.href = result.checkoutUrl
          }
        } else {
          setErrorMessage(result.errorMessage!)
          buttonBlockedRef.current = false
          setButtonBlocked(false)
        }
      })
      .catch(() => setErrorMessage('An error occurred'))
  }

  return (
    <CheckoutResult
      icon={<IslandTropical className="mb-8 mt-1 h-12 w-12 fill-primary" />}
      title={props.request ? t('bookingSuccessTitleRequest') : t('bookingSuccessTitleBooking')}
      description={
        props.reservation && (
          <p className="mb-4 font-semibold">
            {t('bookingSuccessReservation')} <br />
          </p>
        )
      }
      message={props.message || ''}
      actionElement={
        showPayNowButton ? (
          <div className="mb-20 flex w-full max-w-[450px] flex-col justify-center">
            <PayNowButton
              onClick={handlePaymentClick}
              buttonBlockedRef={buttonBlockedRef}
              disabled={buttonBlocked}
            />
            {errorMessage && <CheckoutErrorMessage errorMessage={errorMessage} />}
          </div>
        ) : undefined
      }
    />
  )
}
